/**
 * Utility for generating dynamically colored hydrant SVG icons
 * Used by CustomMap.jsx to display hydrants in their assigned color
 */

// Cache for colored hydrant SVGs to avoid regenerating data URLs
const coloredHydrantCache = {};

// Color mapping for hydrant colors to hex values
const hydrantColorMap = {
  red: '#FF5050',
  yellow: '#FFD700',
  white: '#FFFFFF',
  blue: '#4169E1',
  green: '#32CD32',
  orange: '#FF8C00',
  black: '#2D2D2D',
  violet: '#8A2BE2',
  silver: '#C0C0C0'
};

/**
 * Helper function to darken/lighten a hex color
 * @param {string} color - Hex color (e.g., '#FF5050')
 * @param {number} percent - Percentage to lighten (positive) or darken (negative)
 * @returns {string} Modified hex color
 */
function shadeColor(color, percent) {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Generate a colored hydrant SVG as a data URL
 * Results are cached for performance
 * @param {string} color - Color name (red, yellow, white, blue, green, orange, black, violet, silver)
 * @returns {string} Data URL for the colored SVG
 */
export function getColoredHydrantSvg(color) {
  const colorKey = (color || 'red').toLowerCase().trim();
  
  // Return cached version if available
  if (coloredHydrantCache[colorKey]) {
    return coloredHydrantCache[colorKey];
  }

  // Get the hex color, default to red if not found
  const fillColor = hydrantColorMap[colorKey] || hydrantColorMap.red;
  
  // Determine accent color based on main color brightness
  const darkerAccent = colorKey === 'black' ? '#1A1A1A' : 
                       colorKey === 'white' ? '#E0E0E0' : 
                       shadeColor(fillColor, -25);

  // Hydrant SVG template with dynamic colors
  const svgTemplate = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="512" height="512">
    <path fill="#C7CFE2" d="M409.916,238.89c4.722,0,8.551,3.829,8.551,8.551v34.205c0,4.722-3.829,8.551-8.551,8.551h-34.205V238.89H409.916z"/>
    <path fill="#AFB9D2" d="M375.716,290.205h34.205c4.722,0,8.551-3.829,8.551-8.551v-17.102h-42.756V290.205z"/>
    <path fill="#AFB9D2" d="M102.079,238.898c-4.722,0-8.551,3.829-8.551,8.551v34.205c0,4.722,3.829,8.551,8.551,8.551h34.205v-51.307H102.079z"/>
    <path fill="#959CB5" d="M136.284,290.205h-34.205c-4.722,0-8.551-3.829-8.551-8.551v-17.102h42.756V290.205z"/>
    <path fill="#C7CFE2" d="M264.551,8.017h-17.102c-4.722,0-8.551,3.829-8.551,8.551v34.205h34.205V16.568C273.102,11.845,269.274,8.017,264.551,8.017z"/>
    <path fill="#AFB9D2" d="M264.551,8.017h-17.102c-4.722,0-8.551,3.829-8.551,8.551v34.205H256V16.568C256,11.845,259.829,8.017,264.551,8.017z"/>
    <path fill="${darkerAccent}" d="M370.519,318.608c7.994-1.599,13.748-8.618,13.748-16.77v-74.572c0-8.152-5.754-15.172-13.748-16.77l-29.008-5.803v119.716L370.519,318.608z"/>
    <path fill="${fillColor}" d="M256,42.221c-51.949,0-94.063,42.113-94.063,94.063v350.597h188.125V136.284C350.063,84.335,307.949,42.221,256,42.221z"/>
    <path fill="${darkerAccent}" d="M141.481,210.494c-7.994,1.599-13.748,8.618-13.748,16.77v74.572c0,8.152,5.754,15.172,13.748,16.77l29.008,5.803V204.693L141.481,210.494z"/>
    <path fill="${darkerAccent}" d="M268.268,205.921c-12.412-2.481-24.298-1.09-34.724,3.153c-5.625,2.289-11.747-1.794-11.747-7.865v-64.926c0-41.473,26.875-76.604,64.134-89.117c-16.173-5.432-34.21-6.8-53.111-2.029c-42.217,10.657-70.88,50.046-70.88,93.587v348.156h59.858V327.903c0-6.112,6.173-10.134,11.838-7.839c6.907,2.798,14.454,4.345,22.367,4.345c36.938,0,66.062-33.458,58.718-71.722C310.244,229.382,291.538,210.574,268.268,205.921z"/>
    <path fill="${darkerAccent}" d="M392.818,503.983H119.182c-4.722,0-8.551-3.829-8.551-8.551V478.33c0-4.722,3.829-8.551,8.551-8.551h273.637c4.722,0,8.551,3.829,8.551,8.551v17.102C401.37,500.155,397.541,503.983,392.818,503.983z"/>
    <path fill="${darkerAccent}" d="M367.165,170.489h-222.33c-4.722,0-8.551-3.829-8.551-8.551v-17.102c0-4.722,3.829-8.551,8.551-8.551h222.33c4.722,0,8.551,3.829,8.551,8.551v17.102C375.716,166.66,371.887,170.489,367.165,170.489z"/>
    <path fill="${darkerAccent}" d="M213.244,161.937v-17.102c0-4.722,3.829-8.551,8.551-8.551h-76.96c-4.722,0-8.551,3.829-8.551,8.551v17.102c0,4.722,3.829,8.551,8.551,8.551h76.96C217.073,170.489,213.244,166.66,213.244,161.937z"/>
    <path fill="${darkerAccent}" d="M213.244,495.432V478.33c0-4.722,3.829-8.551,8.551-8.551H119.182c-4.722,0-8.551,3.829-8.551,8.551v17.102c0,4.722,3.829,8.551,8.551,8.551h102.614C217.073,503.983,213.244,500.155,213.244,495.432z"/>
    <circle fill="${darkerAccent}" cx="256" cy="264.551" r="42.756"/>
    <circle fill="#C7CFE2" cx="256" cy="264.551" r="25.653"/>
  </svg>`;

  // Create data URL and cache it
  const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgTemplate)}`;
  coloredHydrantCache[colorKey] = dataUrl;
  
  return dataUrl;
}

// Export the color map in case it's needed elsewhere
export { hydrantColorMap };
